import React, { useState } from "react";
import axios from "./../services/api";
import { useTranslation } from "react-i18next";
import loginIllustration from "illustrations/login.png";
import { useNavigate } from "react-router";
import tokenService from "services/token.service";
import Spinner from "components/functional/spinner";
import styles from "./login.module.css";
import LanguageSelector from "components/functional/language-selector";
import { NavLink } from "react-router";
import useIsMobile from "hooks/detect-mobile-screen";
import { ToastService, ToastComponent } from "services/toast.service";

const Login = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState(localStorage.getItem("email") || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [errors, setErrors] = useState({});
  const isMobile = useIsMobile(980);

  const navigate = useNavigate();

  const validateForm = () => {
    const newErrors = {};
    if (!email) newErrors.email = t("errors.required");
    if (!password) newErrors.password = t("errors.required");
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (!validateForm()) return;
      const response = await axios.post("auth/login", {
        email,
        password,
      });

      const token = response.data.token;
      tokenService.setToken(token);
      rememberMe && localStorage.setItem("email", email);
      navigate("/dashboard");
    } catch (error) {
      console.error("Login failed", error);
      ToastService.showErrorToast(t("toast.login-error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className={styles.container}>
        <ToastComponent />
        <section className={styles["container-left"]}>
          <header className={styles["header-container"]}>
            <div className="company-logo">
              <img src="logo.png" width="26" height="24" alt="" />
              <h1 className="title-2-variant">Kompa</h1>
            </div>
            <LanguageSelector></LanguageSelector>
          </header>
          <div className={styles["container-left-content"]}>
            <form onSubmit={handleLogin} className={styles.wrapper}>
              <h2 className="title-1">{t("login.title")}</h2>
              <div className={styles["wrapper-form"]}>
                <div className="form-group">
                  <label htmlFor="email">{t("login.form.email.title")}</label>
                  <input
                    type="email"
                    autoComplete="username"
                    id="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setErrors({ ...errors, email: null });
                    }}
                    className={errors.email && "error"}
                    placeholder={t("login.form.email.placeholder")}
                  />
                  {errors.email && (
                    <p className="error-message">{t("errors.required")}</p>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="password">
                    {t("login.form.password.title")}
                  </label>
                  <input
                    type="password"
                    autoComplete="current-password"
                    id="password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setErrors({ ...errors, password: null });
                    }}
                    className={errors.password && "error"}
                    placeholder={t("login.form.password.placeholder")}
                  />
                  {errors.password && (
                    <p className="error-message">{t("errors.required")}</p>
                  )}
                </div>
              </div>
              <div className={styles["wrapper-actions"]}>
                <span className={styles["checkbox-link-container"]}>
                  <div className={styles["checkbox-wrapper"]}>
                    <input
                      id="remember"
                      type="checkbox"
                      aria-hidden="true"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                    />
                    <label htmlFor="remember" className="label-2">
                      {t("login.form.remember-me")}
                    </label>
                  </div>
                  <NavLink to="/password" className={styles["forgot-password"]}>
                    {t("login.form.password-forgotten")}
                  </NavLink>
                </span>
                <button
                  disabled={loading}
                  type="submit"
                  className="btn btn--primary"
                >
                  {t("login.form.login")}
                  {loading && <Spinner className="action-loader" />}
                </button>
                <hr className="separator separator-secondary" />
                <span className={styles["registration"]}>
                  <p className="label-2">{t("login.form.registration.text")}</p>
                  <NavLink
                    to="/register"
                    className={styles["registration-link"]}
                  >
                    {t("login.form.registration.link")}
                  </NavLink>
                </span>
              </div>
            </form>
          </div>
        </section>
        {!isMobile && (
          <section className={styles["container-right"]}>
            <div className={styles["container-right-content"]}>
              <img
                alt="illustration"
                className={styles.illustration}
                src={loginIllustration}
                width={400}
              />
              <p className={`${styles.description} subtitle-1`}>
                {t("login.banner.title")}
              </p>
              <p className={`${styles.description} title-1-variant`}>
                {t("login.banner.description")}
              </p>
            </div>
          </section>
        )}
      </div>
    </>
  );
};

export default Login;
